(function (root, factory) {
  const CfgPackage = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { CfgPackage };
  } else {
    root.CfgPackage = CfgPackage;
  }
})(typeof window !== "undefined" ? window : globalThis, function () {
  const BOM_PROP_CAP = 255;
  const SESSION_PREFIX = "rb-cfg-pkg-warned:";
  const PROJECT_DETAILS_VARIANT_ID = 52412839166241;
  const PLAN_VALIDATION_VARIANT_ID = 52412825534753;

  function glassCfgKey(item) {
    const src = String((item && item.source) || "").trim();
    if (src === "panel_custom_standard" || src === "panel_custom_perforated") {
      const w = Math.round(Number(item.lengthIn) || 0);
      const holes = src === "panel_custom_perforated" ? "4" : "0";
      return src + ":" + w + ":h" + holes;
    }
    const variantId = item && item.variantId;
    if (variantId != null && variantId !== "") {
      return src + ":" + variantId;
    }
    return src;
  }

  function customerProperties(properties) {
    const out = {};
    if (!properties || typeof properties !== "object") return out;
    Object.keys(properties).forEach((key) => {
      if (!key || key.charAt(0) === "_") return;
      const value = properties[key];
      if (value == null || value === "") return;
      out[String(key)] = String(value);
    });
    return out;
  }

  function collapseSnapshotEntries(entries) {
    const list = Array.isArray(entries) ? entries : [];
    const byKey = new Map();
    const order = [];
    list.forEach((raw) => {
      const entry = raw && typeof raw === "object" ? raw : {};
      const key = String(entry.k || "");
      const q = Number(entry.q) || 0;
      if (!byKey.has(key)) {
        byKey.set(key, Object.assign({}, entry, { k: key, q: q }));
        order.push(key);
      } else {
        const existing = byKey.get(key);
        existing.q = (Number(existing.q) || 0) + q;
      }
    });
    return order.map((key) => byKey.get(key));
  }

  function encodeRecommendedBom(entries) {
    const collapsed = collapseSnapshotEntries(entries);
    const json = JSON.stringify(collapsed);
    if (json.length <= BOM_PROP_CAP) return { _recommended_bom: json };
    const props = {};
    let n = 1;
    for (let i = 0; i < json.length; i += BOM_PROP_CAP) {
      props["_recommended_bom_" + n] = json.slice(i, i + BOM_PROP_CAP);
      n += 1;
    }
    return props;
  }

  function parseSnapshotJson(raw) {
    if (raw == null || raw === "") return [];
    let text = String(raw).trim();
    if (!text) return [];
    if (text.indexOf("&") !== -1) {
      text = text.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
    }
    try {
      let parsed = JSON.parse(text);
      if (typeof parsed === "string") parsed = JSON.parse(parsed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function decodeRecommendedBom(properties) {
    const props = properties && typeof properties === "object" ? properties : {};
    if (props._recommended_bom) {
      try {
        const parsed = JSON.parse(String(props._recommended_bom));
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }
    const parts = [];
    for (let i = 1; i <= 40; i++) {
      const chunk = props["_recommended_bom_" + i];
      if (chunk == null || chunk === "") break;
      parts.push(String(chunk));
    }
    if (!parts.length) return [];
    try {
      const parsed = JSON.parse(parts.join(""));
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function recQtyFromLive(item) {
    const props = (item && item.properties) || {};
    const fromProp = Number(props._cfgRecQty);
    if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;
    const qty = Number(item && item.quantity);
    return Number.isFinite(qty) ? qty : 0;
  }

  function mergePackageRows(snapshot, liveItems) {
    const live = Array.isArray(liveItems) ? liveItems : [];
    const snap = collapseSnapshotEntries(snapshot);
    const liveByKey = new Map();
    live.forEach((item) => {
      const key = String(((item && item.properties) || {})._cfgKey || "");
      if (key) liveByKey.set(key, item);
    });
    if (!snap.length) {
      return live.map((item) => ({ kind: "live", item, recQty: recQtyFromLive(item) }));
    }
    const used = new Set();
    const rows = [];
    snap.forEach((entry) => {
      const key = String((entry && entry.k) || "");
      if (key && used.has(key)) return;
      const recQty = Number(entry && entry.q) || 0;
      const item = key ? liveByKey.get(key) : null;
      if (item) {
        used.add(key);
        rows.push({ kind: "live", item, recQty, entry });
      } else {
        rows.push({ kind: "placeholder", entry, recQty });
      }
    });
    live.forEach((item) => {
      const key = String(((item && item.properties) || {})._cfgKey || "");
      if (key && used.has(key)) return;
      rows.push({ kind: "live", item, recQty: recQtyFromLive(item) });
    });
    return rows;
  }

  function liveRowCfgKey(row) {
    return String((((row && row.item) || {}).properties || {})._cfgKey || "");
  }

  function placeholderInsertHint(mergedRows, index) {
    const rows = Array.isArray(mergedRows) ? mergedRows : [];
    for (let i = index + 1; i < rows.length; i++) {
      const row = rows[i];
      if (row && row.kind === "live" && row.item && row.item.row && liveRowCfgKey(row)) {
        return { before: row.item.row };
      }
    }
    for (let i = index - 1; i >= 0; i--) {
      const row = rows[i];
      if (row && row.kind === "live" && row.item && row.item.row && liveRowCfgKey(row)) {
        return { after: row.item.row };
      }
    }
    return { afterLocked: true };
  }

  function packageRowKeys(mergedRows) {
    const rows = Array.isArray(mergedRows) ? mergedRows : [];
    const keys = [];
    rows.forEach((row) => {
      if (row && row.kind === "placeholder") {
        const key = String((row.entry && row.entry.k) || "");
        if (key) keys.push(key);
        return;
      }
      const key = liveRowCfgKey(row);
      if (key) keys.push(key);
    });
    return keys;
  }

  function placeholderDisplayTitle(entry, product, fallback) {
    if (entry && entry.t) return String(entry.t);
    if (product && product.title) return String(product.title);
    return fallback == null ? "" : String(fallback);
  }

  function liveOverlayRecQty(merged) {
    if (!merged || merged.kind !== "live") return null;
    if (merged.entry) {
      const fromSnap = Number(merged.entry.q);
      if (Number.isFinite(fromSnap) && fromSnap > 0) return fromSnap;
    }
    const fromProp = Number(((merged.item && merged.item.properties) || {})._cfgRecQty);
    if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;
    return null;
  }

  function isPackageWarned(bundleId, storage) {
    if (!storage || !bundleId) return false;
    try {
      return storage.getItem(SESSION_PREFIX + bundleId) === "1";
    } catch (_) {
      return false;
    }
  }

  function markPackageWarned(bundleId, storage) {
    if (!storage || !bundleId) return;
    try {
      storage.setItem(SESSION_PREFIX + bundleId, "1");
    } catch (_) {}
  }

  function buildReaddPayload({ bundleId, entry, quantity }) {
    const qty = Number(quantity) > 0 ? Number(quantity) : 1;
    const rec = String((entry && entry.q) || qty);
    return {
      id: Number(entry && entry.v),
      quantity: qty,
      properties: Object.assign({}, customerProperties(entry && entry.p), {
        _bundle_id: String(bundleId),
        _cfgKey: String((entry && entry.k) || ""),
        _cfgRecQty: rec,
      }),
    };
  }

  function isLockedVariantId(variantId) {
    const id = Number(variantId);
    return id === PROJECT_DETAILS_VARIANT_ID || id === PLAN_VALIDATION_VARIANT_ID;
  }

  function isPackageRemoveVariantId(variantId) {
    return Number(variantId) === PROJECT_DETAILS_VARIANT_ID;
  }

  return {
    BOM_PROP_CAP,
    PROJECT_DETAILS_VARIANT_ID,
    PLAN_VALIDATION_VARIANT_ID,
    glassCfgKey,
    customerProperties,
    collapseSnapshotEntries,
    encodeRecommendedBom,
    parseSnapshotJson,
    decodeRecommendedBom,
    mergePackageRows,
    liveOverlayRecQty,
    placeholderInsertHint,
    packageRowKeys,
    placeholderDisplayTitle,
    isPackageWarned,
    markPackageWarned,
    buildReaddPayload,
    isLockedVariantId,
    isPackageRemoveVariantId,
  };
});
